import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IInboxActionResult,
  IInboxCancelRequest,
  IInboxOrderRefundRequest,
  IInboxReturnRefundRequest,
} from "../models/messagesInbox/IInboxApi";
import { DefaultResponseWithData } from "../models/system";
import apiConfig from "../utils/apiConfig";
import { conversationPath, messagesInboxKeys } from "./useMessagesInbox";

/** What happened. blocked = sending is switched off on this Wilma, so nothing reached the buyer or eBay. */
export interface IInboxActionOutcome extends IInboxActionResult {
  message: string;
}

/**
 * The API answers 200 with success false and data.blocked when sending is switched off here; that
 * isn't an error, nothing was done. Any other refusal (eBay said no, bad request) is thrown so the
 * caller shows the API's message.
 */
async function outcome(request: Promise<DefaultResponseWithData<IInboxActionResult | null>>): Promise<IInboxActionOutcome> {
  const res = await request;
  if (res.data?.blocked) return { ...res.data, blocked: true, message: res.message };
  if (!res.success) throw new Error(res.message || "Something went wrong – nothing was done.");
  return { ...res.data, blocked: false, message: res.message };
}

interface IReplyVariables {
  conversationId: string;
  body: string;
}

interface IOrderVariables {
  partId: string;
  conversationId: string;
}

export default function useMessagesInboxMutations(componentName = "useMessagesInboxMutations") {
  const { post } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();

  const refreshAfter = (conversationId: string, partId?: string) => {
    queryClient.invalidateQueries(messagesInboxKeys.thread(conversationId));
    queryClient.invalidateQueries(messagesInboxKeys.conversations(true));
    if (partId) queryClient.invalidateQueries(messagesInboxKeys.partContextAll(partId));
  };

  const reply = useMutation(
    ({ conversationId, body }: IReplyVariables) =>
      outcome(post<{ body: string }, DefaultResponseWithData<IInboxActionResult>>(`${conversationPath(conversationId)}/reply`, { body })),
    {
      onSuccess: (data, { conversationId }) => {
        if (!data.blocked) refreshAfter(conversationId);
      },
    }
  );

  const cancelOrder = useMutation(
    ({ partId, ...body }: IOrderVariables & IInboxCancelRequest) =>
      outcome(post<IInboxCancelRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/orders/${encodeURIComponent(partId)}/cancel`, { reason: body.reason })),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  const refundOrder = useMutation(
    ({ partId, conversationId, ...body }: IOrderVariables & IInboxOrderRefundRequest) =>
      outcome(post<IInboxOrderRefundRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/orders/${encodeURIComponent(partId)}/refund`, body)),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  const refundReturn = useMutation(
    ({ returnId, partId, conversationId, ...body }: IOrderVariables & IInboxReturnRefundRequest & { returnId: string }) =>
      outcome(post<IInboxReturnRefundRequest, DefaultResponseWithData<IInboxActionResult>>(`/messagesinbox/returns/${encodeURIComponent(returnId)}/refund`, body)),
    {
      onSuccess: (data, { conversationId, partId }) => {
        if (!data.blocked) refreshAfter(conversationId, partId);
      },
    }
  );

  return { reply, cancelOrder, refundOrder, refundReturn };
}
